/**
 * Slim "the refresh failed, what you're looking at is old" strip.
 *
 * Exists because of a specific failure mode: the detail surfaces used to
 * render their query error ONLY inside the `!data` branch. Once a page had
 * loaded anything at all, a subsequent failed refetch was completely silent —
 * the numbers on screen simply stopped being current, with no indication
 * whatsoever. On a billing surface that is the worst possible outcome: stale
 * figures presented with the full confidence of fresh ones.
 *
 * So this renders *over* good-looking data, on purpose. It's deliberately
 * quiet (one line, amber rather than red — nothing is broken, the data is
 * just old) and dismissible, because a PM who has read it and wants the
 * screen back shouldn't have to look at it while they finish what they were
 * doing.
 *
 * Dismissal lasts until the next failed fetch, not until the error changes.
 * Callers `key` the banner on the error message, and `run()` clears `error` to
 * null before each attempt — so any subsequent failure, including an identical
 * one, remounts this component and resets `dismissed`. That is the intended
 * bias: silence is what caused the bug, so staleness re-announces itself every
 * time it is re-confirmed rather than being permanently silenced by one click.
 * On these surfaces refreshes are focus-driven (30s throttle), not polled, so
 * in practice it reappears when the PM returns to the tab and it fails again.
 */
import { useState } from 'react';

export interface StaleDataBannerProps {
  /** The failed-refresh error. Renders nothing when null. */
  error: Error | null;
  /** Wired to the query's refetch(). */
  onRetry: () => void;
  /**
   * Shown instead of the generic line when the caller has better context.
   * Defaults to the standard copy.
   */
  message?: string;
}

export function StaleDataBanner({ error, onRetry, message }: StaleDataBannerProps) {
  const [dismissed, setDismissed] = useState(false);
  if (!error || dismissed) return null;

  return (
    <div role="status" style={barStyle}>
      <span aria-hidden style={{ color: 'var(--warning)', flexShrink: 0 }}>
        ⚠
      </span>
      <span style={textStyle}>
        {message ?? "Couldn't refresh — showing last loaded data."}
      </span>
      <button type="button" className="btn-link" onClick={onRetry}>
        Retry
      </button>
      <button
        type="button"
        className="btn-link"
        aria-label="Dismiss"
        onClick={() => setDismissed(true)}
        style={{ color: 'var(--ink-3)' }}
      >
        Dismiss
      </button>
    </div>
  );
}

const barStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
  padding: '8px 12px',
  marginBottom: 12,
  background: 'var(--surface-warm)',
  borderLeft: '3px solid var(--warning)',
  border: '1px solid var(--rule-strong)',
  borderLeftWidth: 3,
  borderLeftColor: 'var(--warning)',
};

const textStyle: React.CSSProperties = {
  fontSize: 13,
  lineHeight: 1.35,
  color: 'var(--ink-2)',
  flex: 1,
  minWidth: 200,
};
